import Modifier from 'ember-modifier';
import Color from 'colorjs.io'
import extractStyleParams from '../utils/extract-style-params';

export default class ThemeColors extends Modifier {
  element = null;
  containerElement = null;
  defaults = {};

  buildColorsFromBackground(color) {
    let style = {}
    if (!color) {
      return style;
    }

    try {
      style['color-background'] = new Color(color).toString({format: 'hex'});
      style['color-background-dark'] = new Color(color).set({
        'oklch.l': l => l * 0.9
      }).toString({format: 'hex'})
      style['color-background-light'] = new Color(color).set({
        'oklch.l': l => l * 1.1
      }).toString({format: 'hex'})

      style['color-background-gradient-1'] = new Color(color).set({
        'oklch.h': h => h - 15
      }).toString({format: 'hex'})
      style['color-background-gradient-2'] = new Color(color).toString({format: 'hex'})
      style['color-background-gradient-3'] = new Color(color).set({
        'oklch.h': h => h + 15
      }).toString({format: 'hex'})
    } catch(e) {
      console.error(`error building background colors for ${color}`)
      console.error(e)
    } finally {
      // console.log(`built background colors from ${color}`, style)
      return style
    }
  }

  buildColorsFromText(color) {
    let style = {}

    if (!color) {
      return style;
    }

    try {
      let colorObject = new Color(color);
      let swag = 0.2;
      style['color-text'] = colorObject.toString({format: 'hex'});
      if (colorObject.contrast('white', "WCAG21") > colorObject.contrast('black', "WCAG21")) {
        style['color-text-muted'] = new Color(color).set({'oklch.l': (l) => l + swag }).toString({format: 'hex'});
      } else {
        style['color-text-muted'] = new Color(color).set({'oklch.l': (l) => l - swag }).toString({format: 'hex'});
      }

    } catch(e) {
      console.error(`error building text colors for`, color)
      console.error(e)
    } finally {
      // console.log(`built text colors from ${color}`, style)
      return style
    }
  }

  buildColorsFromHighlight(color) {
    let style = {}

    if (!color) {
      return style;
    }

    try {
      style['color-highlight'] = new Color(color).toString({format: 'hex'});
      style['color-highlight-dark'] = new Color(color).set({
        'oklch.l': l => l * 1.2
      }).toString({format: 'hex'})
      style['color-highlight-light'] = new Color(color).set({
        'oklch.l': l => l * 0.8
      }).toString({format: 'hex'})
      return style;
    } catch(e) {
      console.error(`error building highlight colors for`, color)
      console.error(e)
    } finally {
      return style
    }

  }

  buildContrastingTextColorFromBackground(color) {
    let style = {}

    if (!color) {
      return style;
    }

    try {
      let bg = new Color(color)
      let text1 = new Color(color).set("hsl.l", l => 95)
      let text2 = new Color(color).set("hsl.l", l => 5)

      if (bg.contrast(text1, "WCAG21") > bg.contrast(text2, "WCAG21")) {
        return text1
      } else {
        return text2
      }
    }
    catch(e) {
      console.error(e)
    }
  }


  get calculatedStyle() {
    let style = {};
    let backgroundColors = {}, textColors = {}, highlightColors = {}
    try {
      let customStyle = { ...this.defaults, ...extractStyleParams(this.containerElement) };
      // console.log('specified style params', customStyle)
      backgroundColors = this.buildColorsFromBackground(customStyle['color-background'] || "#333")

      if (customStyle['color-text']) {
        textColors = this.buildColorsFromText(customStyle['color-text'])
      } else {
        let defaultTextColor = this.buildContrastingTextColorFromBackground(backgroundColors['color-background'])
        textColors = this.buildColorsFromText(defaultTextColor.toString())
      }

      highlightColors = this.buildColorsFromHighlight(customStyle['color-highlight'] || textColors['color-text'])

      style = {...backgroundColors, ...highlightColors, ...textColors }

      // Colors we don't derive variants for (e.g. color-event-background) pass through untouched.
      Object.keys(customStyle).forEach((key) => {
        if (key.startsWith('color-') && !(key in style) && customStyle[key]) {
          style[key] = customStyle[key];
        }
      });
    } catch (e) {
      console.error(e);
    }

    return style;
  }

  convertVariables() {
    Object.keys(this.calculatedStyle).forEach((key) => {
      this.element.style.setProperty(
        `--dt-${key}`,
        `${this.calculatedStyle[key]}`
      );
    });
  }

  clearVariables() {
    [...this.element.style]
      .filter((name) => name.startsWith('--dt-color-'))
      .forEach((name) => this.element.style.removeProperty(name));
  }

  // defaults: station-wide colors the embed's own attributes override. null leaves the element bare.
  modify(element, positionalArgs, { containerElementId, defaults }) {
    this.element = element;
    if (defaults === null) {
      this.observer?.disconnect();
      this.clearVariables();
      return;
    }
    this.defaults = defaults || {};
    this.containerElement = containerElementId
      ? document.getElementById(containerElementId)
      : element;

    let MutationObserver =
      window.MutationObserver || window.WebKitMutationObserver;

    // Modify runs again when the station theme arrives, so the earlier observer must go.
    this.observer?.disconnect();
    this.observer = new MutationObserver(() => this.convertVariables());
    this.observer.observe(this.containerElement, {
      attributes: true,
    });
    this.convertVariables();

    return () => {
      this.observer.disconnect();
    };
  }
}
